/**
 * Static spatial model for Alpine Woodsmoke Unit 4, Falls Creek.
 *
 * Coordinate system: X = east, Z = south, Y = up. The model is deliberately
 * asset-neutral so the scene renderer, room UI, and pedestrian simulation all
 * share one dependable representation of the apartment.
 */

import type { NavGraph, NavNode, NavNodeKind } from './conference-twin-data';

export type RoomId =
  | 'master-bedroom'
  | 'bedroom-2'
  | 'bedroom-3'
  | 'ensuite'
  | 'main-bathroom'
  | 'powder-room-1'
  | 'powder-room-2'
  | 'laundry'
  | 'lounge'
  | 'kitchen-dining'
  | 'balcony';

export type DoorSide = 'north' | 'south' | 'east' | 'west';

export interface RoomDef {
  id: RoomId;
  name: string;
  shortName: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  floorColor: string;
  wallColor: string;
  description: string;
  sleepingCapacity: number;
  amenities: string[];
  doorSide: DoorSide;
}

export interface WallSegment {
  id: string;
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  height?: number;
  thickness?: number;
  material: 'plaster' | 'stone' | 'glass' | 'balustrade';
}

export interface DoorOpening {
  id: string;
  roomId: RoomId;
  x: number;
  z: number;
  width: number;
  side: DoorSide;
  type: 'swing' | 'sliding' | 'opening';
}

export interface FurnitureAnchor {
  id: string;
  roomId: RoomId;
  kind:
    | 'queen-bed'
    | 'split-king-bed'
    | 'trundle-bed'
    | 'bunk-bed'
    | 'wardrobe'
    | 'sofa'
    | 'armchair'
    | 'fireplace'
    | 'kitchen-island'
    | 'kitchen-counter'
    | 'dining-table'
    | 'washer'
    | 'dryer'
    | 'drying-rack'
    | 'bath-spa'
    | 'shower'
    | 'vanity'
    | 'toilet'
    | 'balcony-seating';
  x: number;
  z: number;
  rotationY?: number;
  width?: number;
  depth?: number;
  label?: string;
}

export interface CameraPreset {
  id: 'overview' | 'lounge' | 'master-suite' | 'bedrooms' | 'kitchen-dining' | 'balcony';
  label: string;
  position: [number, number, number];
  target: [number, number, number];
}

export type WoodsmokeNavNodeKind = NavNodeKind;
export type WoodsmokeNavNode = NavNode;
export type WoodsmokeNavGraph = NavGraph;

/** Exterior footprint, including the valley-facing balcony. */
export const FLOOR_BOUNDS = {
  minX: -22,
  maxX: 22,
  minZ: -16,
  maxZ: 18,
  centerX: 0,
  centerZ: 1
} as const;

export const ROOMS: RoomDef[] = [
  {
    id: 'master-bedroom', name: 'Master Suite', shortName: 'Master', x: -15, z: -8, width: 12, depth: 12, height: 3.2,
    floorColor: '#b99671', wallColor: '#ece6dc', doorSide: 'east', sleepingCapacity: 2,
    description: 'A private queen suite with a walk-in robe and direct access to the ensuite.',
    amenities: ['Queen bed', 'Walk-in robe', 'Ensuite access']
  },
  {
    id: 'bedroom-2', name: 'Bedroom 2', shortName: 'Bed 2', x: -3.5, z: -9, width: 10, depth: 10, height: 3.2,
    floorColor: '#b99671', wallColor: '#ece6dc', doorSide: 'south', sleepingCapacity: 3,
    description: 'Flexible guest room with a splitable king bed and pull-out single trundle.',
    amenities: ['Splitable king bed', 'Single trundle', 'Wardrobe']
  },
  {
    id: 'bedroom-3', name: 'Bedroom 3', shortName: 'Bed 3', x: 7.5, z: -9, width: 11, depth: 10, height: 3.2,
    floorColor: '#b99671', wallColor: '#ece6dc', doorSide: 'south', sleepingCapacity: 3,
    description: 'Family bedroom arranged for three sleepers with bunks and a single bed.',
    amenities: ['Bunk bed', 'Single bed', 'Wardrobe']
  },
  {
    id: 'ensuite', name: 'Master Ensuite', shortName: 'Ensuite', x: -15, z: 0, width: 10, depth: 4, height: 3.2,
    floorColor: '#b8c3c4', wallColor: '#f3f4f0', doorSide: 'north', sleepingCapacity: 0,
    description: 'Private master bathroom with shower, vanity and toilet.', amenities: ['Shower', 'Vanity', 'Toilet']
  },
  {
    id: 'main-bathroom', name: 'Family Bathroom', shortName: 'Bath', x: -4, z: -1.5, width: 9, depth: 5, height: 3.2,
    floorColor: '#b8c3c4', wallColor: '#f3f4f0', doorSide: 'east', sleepingCapacity: 0,
    description: 'Generous family bathroom centred on a deep bath and spa.', amenities: ['Bath / spa', 'Separate shower', 'Double vanity']
  },
  {
    id: 'powder-room-1', name: 'Powder Room 1', shortName: 'Powder 1', x: 5, z: -1.5, width: 4, depth: 5, height: 3.2,
    floorColor: '#c3ccca', wallColor: '#f3f4f0', doorSide: 'south', sleepingCapacity: 0,
    description: 'Convenient guest powder room near the bedroom hall.', amenities: ['Toilet', 'Vanity']
  },
  {
    id: 'powder-room-2', name: 'Powder Room 2', shortName: 'Powder 2', x: 17, z: 0, width: 6, depth: 4, height: 3.2,
    floorColor: '#c3ccca', wallColor: '#f3f4f0', doorSide: 'west', sleepingCapacity: 0,
    description: 'Second powder room off the kitchen and laundry wing.', amenities: ['Toilet', 'Vanity']
  },
  {
    id: 'laundry', name: 'Laundry & Drying Room', shortName: 'Laundry', x: 17, z: -9, width: 6, depth: 10, height: 3.2,
    floorColor: '#aeb8ba', wallColor: '#edf0ed', doorSide: 'west', sleepingCapacity: 0,
    description: 'Dedicated utility space for washing, drying and ski gear storage.', amenities: ['Washer', 'Dryer', 'Ski drying racks']
  },
  {
    id: 'lounge', name: 'Lounge & Fireplace', shortName: 'Lounge', x: -7, z: 8, width: 28, depth: 12, height: 3.6,
    floorColor: '#aa835f', wallColor: '#eadfd2', doorSide: 'east', sleepingCapacity: 0,
    description: 'Warm open lounge focused around a central stone open fireplace and valley outlook.', amenities: ['Stone fireplace', 'Lounge seating', 'Floor-to-ceiling glazing']
  },
  {
    id: 'kitchen-dining', name: 'Kitchen & Dining', shortName: 'Kitchen', x: 13.5, z: 8, width: 13, depth: 12, height: 3.6,
    floorColor: '#aa835f', wallColor: '#eadfd2', doorSide: 'west', sleepingCapacity: 0,
    description: 'European self-contained kitchen with an island and a dining setting beside the balcony.', amenities: ['Kitchen island', 'European appliances', 'Dining for eight']
  },
  {
    id: 'balcony', name: 'Valley Balcony', shortName: 'Balcony', x: 0, z: 16, width: 40, depth: 4, height: 1.2,
    floorColor: '#8e725a', wallColor: '#3e4b4b', doorSide: 'north', sleepingCapacity: 0,
    description: 'Covered outdoor terrace with a broad valley-facing alpine outlook.', amenities: ['Mountain views', 'Outdoor seating', 'Glass balustrade']
  }
];

/** Continuous exterior shell plus primary room divisions. Door gaps are represented by DOORS. */
export const STRUCTURAL_WALLS: WallSegment[] = [
  { id: 'exterior-north', x1: -22, z1: -16, x2: 22, z2: -16, material: 'plaster' },
  { id: 'exterior-west', x1: -22, z1: -16, x2: -22, z2: 14, material: 'plaster' },
  { id: 'exterior-east', x1: 22, z1: -16, x2: 22, z2: 14, material: 'plaster' },
  { id: 'balcony-west', x1: -20, z1: 14, x2: -20, z2: 18, height: 1.2, material: 'balustrade' },
  { id: 'balcony-south', x1: -20, z1: 18, x2: 20, z2: 18, height: 1.2, material: 'glass' },
  { id: 'balcony-east', x1: 20, z1: 18, x2: 20, z2: 14, height: 1.2, material: 'balustrade' },
  { id: 'bedroom-wing-divider', x1: -22, z1: -3, x2: 20, z2: -3, material: 'plaster' },
  { id: 'master-east', x1: -9, z1: -16, x2: -9, z2: 2, material: 'plaster' },
  { id: 'bedroom-2-east', x1: 1.5, z1: -16, x2: 1.5, z2: -4, material: 'plaster' },
  { id: 'bedroom-3-east', x1: 13, z1: -16, x2: 13, z2: -4, material: 'plaster' },
  { id: 'laundry-west', x1: 14, z1: -16, x2: 14, z2: -3, material: 'plaster' },
  { id: 'ensuite-north', x1: -20, z1: 2, x2: -10, z2: 2, material: 'plaster' },
  { id: 'bath-east', x1: 0.5, z1: -4, x2: 0.5, z2: 1, material: 'plaster' },
  { id: 'powder-1-east', x1: 7, z1: -4, x2: 7, z2: 1, material: 'plaster' },
  { id: 'service-wing-divider', x1: 14, z1: -3, x2: 14, z2: 2, material: 'plaster' },
  { id: 'fireplace-feature', x1: -7, z1: 2, x2: -1, z2: 2, height: 3.2, thickness: 0.7, material: 'stone' },
  { id: 'living-glazing', x1: -20, z1: 14, x2: 20, z2: 14, height: 3.2, material: 'glass' }
];

export const DOORS: DoorOpening[] = [
  { id: 'master-hall', roomId: 'master-bedroom', x: -9, z: -8, width: 1.2, side: 'east', type: 'swing' },
  { id: 'bedroom-2-hall', roomId: 'bedroom-2', x: -3.5, z: -4, width: 1.1, side: 'south', type: 'swing' },
  { id: 'bedroom-3-hall', roomId: 'bedroom-3', x: 7.5, z: -4, width: 1.1, side: 'south', type: 'swing' },
  { id: 'laundry-hall', roomId: 'laundry', x: 14, z: -9, width: 1.1, side: 'west', type: 'swing' },
  { id: 'ensuite-master', roomId: 'ensuite', x: -15, z: -2, width: 1, side: 'north', type: 'swing' },
  { id: 'bath-hall', roomId: 'main-bathroom', x: 0.5, z: -1.5, width: 1, side: 'east', type: 'swing' },
  { id: 'powder-1-hall', roomId: 'powder-room-1', x: 5, z: 1, width: 0.9, side: 'south', type: 'swing' },
  { id: 'powder-2-kitchen', roomId: 'powder-room-2', x: 14, z: 0, width: 0.9, side: 'west', type: 'swing' },
  { id: 'lounge-hall', roomId: 'lounge', x: 7, z: 6, width: 1.6, side: 'east', type: 'opening' },
  { id: 'kitchen-lounge', roomId: 'kitchen-dining', x: 7, z: 8, width: 2.4, side: 'west', type: 'opening' },
  { id: 'balcony-lounge', roomId: 'balcony', x: -7, z: 14, width: 3, side: 'north', type: 'sliding' },
  { id: 'balcony-kitchen', roomId: 'balcony', x: 12, z: 14, width: 2.6, side: 'north', type: 'sliding' }
];

export const FURNITURE_ANCHORS: FurnitureAnchor[] = [
  { id: 'master-queen', roomId: 'master-bedroom', kind: 'queen-bed', x: -16, z: -10, rotationY: 0, width: 2.2, depth: 2.4 },
  { id: 'master-robe', roomId: 'master-bedroom', kind: 'wardrobe', x: -20, z: -5, rotationY: Math.PI / 2 },
  { id: 'bed-2-king', roomId: 'bedroom-2', kind: 'split-king-bed', x: -4, z: -10, width: 2.2, depth: 2.2 },
  { id: 'bed-2-trundle', roomId: 'bedroom-2', kind: 'trundle-bed', x: -4, z: -7.4, width: 2, depth: 0.9 },
  { id: 'bed-3-bunks', roomId: 'bedroom-3', kind: 'bunk-bed', x: 5, z: -10, rotationY: Math.PI / 2 },
  { id: 'bed-3-single', roomId: 'bedroom-3', kind: 'trundle-bed', x: 10, z: -10 },
  { id: 'ensuite-shower', roomId: 'ensuite', kind: 'shower', x: -18.5, z: 0 },
  { id: 'ensuite-vanity', roomId: 'ensuite', kind: 'vanity', x: -13, z: 0 },
  { id: 'main-bath-spa', roomId: 'main-bathroom', kind: 'bath-spa', x: -5.5, z: -2 },
  { id: 'main-bath-shower', roomId: 'main-bathroom', kind: 'shower', x: -1.5, z: -2 },
  { id: 'laundry-washer', roomId: 'laundry', kind: 'washer', x: 16, z: -12 },
  { id: 'laundry-dryer', roomId: 'laundry', kind: 'dryer', x: 18, z: -12 },
  { id: 'laundry-ski-rack', roomId: 'laundry', kind: 'drying-rack', x: 18.5, z: -6 },
  { id: 'lounge-fireplace', roomId: 'lounge', kind: 'fireplace', x: -4, z: 3, rotationY: 0, label: 'Stone open fireplace' },
  { id: 'lounge-sofa', roomId: 'lounge', kind: 'sofa', x: -10, z: 8, rotationY: Math.PI / 2 },
  { id: 'lounge-chair', roomId: 'lounge', kind: 'armchair', x: -3, z: 9, rotationY: -Math.PI / 2 },
  { id: 'kitchen-island', roomId: 'kitchen-dining', kind: 'kitchen-island', x: 12, z: 6, width: 3.2, depth: 1.2 },
  { id: 'kitchen-counter', roomId: 'kitchen-dining', kind: 'kitchen-counter', x: 18.5, z: 8, rotationY: Math.PI / 2 },
  { id: 'dining-table', roomId: 'kitchen-dining', kind: 'dining-table', x: 13, z: 11, width: 3.2, depth: 1.2 },
  { id: 'balcony-seating', roomId: 'balcony', kind: 'balcony-seating', x: 0, z: 16.5, width: 4, depth: 1.4 }
];

export const CAMERA_PRESETS: CameraPreset[] = [
  { id: 'overview', label: 'Overview', position: [28, 42, 36], target: [0, 0, 1] },
  { id: 'lounge', label: 'Lounge & Fireplace', position: [-18, 15, 20], target: [-6, 1.2, 7] },
  { id: 'master-suite', label: 'Master Suite', position: [-25, 13, -4], target: [-15, 1, -7] },
  { id: 'bedrooms', label: 'Bedrooms 2 & 3', position: [5, 18, -22], target: [2, 1, -9] },
  { id: 'kitchen-dining', label: 'Kitchen & Dining', position: [24, 15, 18], target: [13, 1, 8] },
  { id: 'balcony', label: 'Balcony View', position: [0, 10, 25], target: [0, 1, 16] }
];

const NAV_NODES: WoodsmokeNavNode[] = [
  { id: 'hall-west', x: -8.8, z: -1, kind: 'corridor' },
  { id: 'hall-central', x: 2, z: -1, kind: 'corridor' },
  { id: 'hall-east', x: 11, z: -1, kind: 'corridor' },
  { id: 'living-threshold', x: 7, z: 4, kind: 'corridor' },
  { id: 'master-door', x: -9, z: -8, kind: 'door', roomId: 'master-bedroom' },
  { id: 'master-centre', x: -15, z: -8, kind: 'room', roomId: 'master-bedroom' },
  { id: 'ensuite-centre', x: -15, z: 0, kind: 'room', roomId: 'ensuite' },
  { id: 'bedroom-2-door', x: -3.5, z: -4, kind: 'door', roomId: 'bedroom-2' },
  { id: 'bedroom-2-centre', x: -3.5, z: -9, kind: 'room', roomId: 'bedroom-2' },
  { id: 'bedroom-3-door', x: 7.5, z: -4, kind: 'door', roomId: 'bedroom-3' },
  { id: 'bedroom-3-centre', x: 7.5, z: -9, kind: 'room', roomId: 'bedroom-3' },
  { id: 'laundry-door', x: 14, z: -9, kind: 'door', roomId: 'laundry' },
  { id: 'laundry-centre', x: 17, z: -9, kind: 'room', roomId: 'laundry' },
  { id: 'bath-centre', x: -4, z: -1.5, kind: 'room', roomId: 'main-bathroom' },
  { id: 'powder-1-centre', x: 5, z: -1.5, kind: 'room', roomId: 'powder-room-1' },
  { id: 'powder-2-centre', x: 17, z: 0, kind: 'room', roomId: 'powder-room-2' },
  { id: 'lounge-centre', x: -7, z: 8, kind: 'room', roomId: 'lounge' },
  { id: 'fireplace', x: -4, z: 4, kind: 'poi', roomId: 'lounge' },
  { id: 'kitchen-centre', x: 13.5, z: 8, kind: 'room', roomId: 'kitchen-dining' },
  { id: 'balcony-centre', x: 0, z: 16, kind: 'room', roomId: 'balcony' }
];

const NAV_EDGES: readonly (readonly [string, string])[] = [
  ['hall-west', 'hall-central'], ['hall-central', 'hall-east'], ['hall-central', 'living-threshold'],
  ['hall-west', 'master-door'], ['master-door', 'master-centre'], ['master-centre', 'ensuite-centre'],
  ['hall-west', 'bedroom-2-door'], ['bedroom-2-door', 'bedroom-2-centre'],
  ['hall-east', 'bedroom-3-door'], ['bedroom-3-door', 'bedroom-3-centre'],
  ['hall-east', 'laundry-door'], ['laundry-door', 'laundry-centre'],
  ['hall-west', 'bath-centre'], ['hall-central', 'powder-1-centre'], ['hall-east', 'powder-2-centre'],
  ['living-threshold', 'lounge-centre'], ['lounge-centre', 'fireplace'], ['living-threshold', 'kitchen-centre'],
  ['lounge-centre', 'balcony-centre'], ['kitchen-centre', 'balcony-centre']
];

export const ROOM_BY_ID = new Map(ROOMS.map((room) => [room.id, room]));

export function getRoom(roomId: RoomId): RoomDef | undefined {
  return ROOM_BY_ID.get(roomId);
}

function distance(from: WoodsmokeNavNode, to: WoodsmokeNavNode): number {
  return Math.hypot(to.x - from.x, to.z - from.z);
}

function buildNavigationGraph(): WoodsmokeNavGraph {
  const nodeById = new Map(NAV_NODES.map((node) => [node.id, node]));
  const adjacency = new Map(NAV_NODES.map((node) => [node.id, [] as { id: string; cost: number }[]]));

  for (const [fromId, toId] of NAV_EDGES) {
    const from = nodeById.get(fromId);
    const to = nodeById.get(toId);
    if (!from || !to) continue;
    const cost = distance(from, to);
    adjacency.get(fromId)?.push({ id: toId, cost });
    adjacency.get(toId)?.push({ id: fromId, cost });
  }

  return { nodes: NAV_NODES, nodeById, adjacency };
}

/** Connected interior graph used by PedestrianSimulation route selection. */
export const WOODSMOKE_NAV_GRAPH = buildNavigationGraph();
